import { LitElement, html, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import innerStyles from './outline-tooltip.inner.pcss?inline';
import globalStyles from './outline-tooltip.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

/**
 * @element outline-tooltip
 * @description
 * A component that displays a small popup of descriptive text (tooltip) when a
 * user hovers over or focuses on a trigger element. The tooltip's position is
 * dynamically calculated to ensure visibility within the viewport.
 * This component follows the WAI-ARIA Tooltip pattern for accessibility.
 * @see {@link https://www.w3.org/WAI/ARIA/apg/patterns/tooltip/}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/tooltip_role}
 *
 * @slot - Default slot. The content that triggers the tooltip on hover/focus.
 * @slot tooltip-content - The content to be displayed within the tooltip popup.
 *
 * @fires outline-tooltip-show - Dispatched when the tooltip becomes visible.
 * @fires outline-tooltip-hide - Dispatched when the tooltip becomes hidden.
 *
 * @prop {string} defaultPosition - Preferred position of the tooltip. Options are 'top', 'right', 'bottom', 'left'. Defaults to 'right'.
 * @prop {string} tooltipId - Optional ID for the tooltip panel. If not provided, a unique ID will be generated.
 * @prop {boolean} debug - When true, logs component information to the console.
 */
@customElement('outline-tooltip')
export class OutlineTooltip extends LitElement {
    adoptedStyleSheets = new AdoptedStyleSheets(this, {
      globalCSS: globalStyles,
      encapsulatedCSS: innerStyles,
    });

  @property({ type: String, reflect: true, attribute: 'default-position'})
  defaultPosition: 'top' | 'right' | 'bottom' | 'left' = 'right';

  @property({ type: String, attribute: 'tooltip-id', reflect: true })
  tooltipId?: string;

  @property({ type: Boolean, reflect: true })
  debug = false;

  @state()
  private _isOpen = false;

  @state()
  private _currentPosition: 'top' | 'right' | 'bottom' | 'left';

  // Track position changes to prevent flickering
  private _positionChangeCount = 0;

  // --- Element References ---
  @query('#trigger')
  private triggerEl!: HTMLElement;

  // panelEl can't use @query because it has a dynamic ID
  private panelEl: HTMLElement | null = null;

  // --- Timer ---
  private timerId?: number;

  private debugLog(message: string, ...args: unknown[]) {
    if (this.debug) {
      console.log(`🎠 ${message}`, ...args);
    }
  }

  constructor() {
    super();
    // Initialize the current position with the default position
    this._currentPosition = this.defaultPosition;
  }

  connectedCallback() {
    super.connectedCallback();
    // Generate a unique ID if none is provided
    if (!this.tooltipId) {
      this.tooltipId = `outline-tooltip-${Math.random().toString(36).substring(2, 9)}`;
    }
    this.debugLog('Connected', { id: this.tooltipId });
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.debugLog('Disconnected');
    document.removeEventListener('keydown', this.handleEscapeKey);
    if (this.triggerEl) {
      this.triggerEl.removeEventListener('mouseenter', this.handleTriggerInteractionStart);
      this.triggerEl.removeEventListener('mouseleave', this.handleTriggerInteractionEnd);
      this.triggerEl.removeEventListener('focusin', this.handleTriggerInteractionStart);
      this.triggerEl.removeEventListener('focusout', this.handleTriggerInteractionEnd);
      this.triggerEl.removeEventListener('touchstart', this.handleTriggerInteractionStart);
      this.triggerEl.removeEventListener('touchend', this.handleTriggerInteractionEnd);
    }
    if (this.panelEl) {
      this.panelEl.removeEventListener('mouseenter', this.handlePanelInteractionStart);
      this.panelEl.removeEventListener('mouseleave', this.handlePanelInteractionEnd);
    }
    this.clearTimer();
  }

  firstUpdated() {
    // triggerEl is handled by @query decorator
    this.panelEl = this.shadowRoot?.querySelector(`#${this.tooltipId}`) as HTMLElement;
    this.debugLog('First updated', {
      triggerEl: this.triggerEl,
      panelEl: this.panelEl
    });

    if (this.triggerEl) {
      this.triggerEl.addEventListener('mouseenter', this.handleTriggerInteractionStart);
      this.triggerEl.addEventListener('mouseleave', this.handleTriggerInteractionEnd);
      this.triggerEl.addEventListener('focusin', this.handleTriggerInteractionStart);
      this.triggerEl.addEventListener('focusout', this.handleTriggerInteractionEnd);
      this.triggerEl.addEventListener('touchstart', this.handleTriggerInteractionStart);
      this.triggerEl.addEventListener('touchend', this.handleTriggerInteractionEnd);
    }

    if (this.panelEl) {
      this.panelEl.addEventListener('mouseenter', this.handlePanelInteractionStart);
      this.panelEl.addEventListener('mouseleave', this.handlePanelInteractionEnd);
    }
  }

  private handleEscapeKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this._isOpen) {
      this.debugLog('Escape key pressed');
      this.hideTooltip();
    }
  };

  private handleTriggerInteractionStart = () => {
    this.debugLog('Trigger interaction start');
    this.clearTimer();
    this.timerId = window.setTimeout(() => this.showTooltip(), 200);
  };

  private handleTriggerInteractionEnd = (event: MouseEvent | FocusEvent | TouchEvent) => {
    this.debugLog('Trigger interaction end', event.type);
    this.clearTimer();

    // Don't hide if focus/mouse moved to the panel
    // TouchEvent doesn't have relatedTarget property
    if (this.panelEl &&
        'relatedTarget' in event &&
        (event.relatedTarget === this.panelEl ||
         this.panelEl.contains(event.relatedTarget as Node))) {
      this.debugLog('Related target is panel, not hiding');
      return;
    }

    this.timerId = window.setTimeout(() => this.hideTooltip(), 100);
  };

  private handlePanelInteractionStart = () => {
    this.debugLog('Panel interaction start');
    this.clearTimer();
  };

  private handlePanelInteractionEnd = () => {
    this.debugLog('Panel interaction end');
    this.clearTimer();
    this.timerId = window.setTimeout(() => this.hideTooltip(), 100);
  };

  private clearTimer() {
    if (this.timerId) {
      this.debugLog('Clearing timer', this.timerId);
      clearTimeout(this.timerId);
      this.timerId = undefined;
    }
  }

  /**
   * Calculates the best position for the tooltip based on available space
   * and the defaultPosition preference, ensuring it fits within viewport.
   */
  private calculatePosition() {
    if (!this.triggerEl || !this.panelEl) return;
    this.debugLog('Calculating position');

    // Reset any previous position adjustments
    this.resetPositionAdjustments();

    // Temporarily make the panel visible but transparent for size calculation
    this.panelEl.style.visibility = 'hidden';
    this.panelEl.style.display = 'block';

    // Get sizes and positions
    const triggerRect = this.triggerEl.getBoundingClientRect();
    const panelRect = this.panelEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Get tooltip offset from CSS variable or default to 8
    const offsetPx = parseInt(
      getComputedStyle(this).getPropertyValue('--outline-tooltip-offset') || '8',
      10
    );

    // Calculate available space in each direction
    const spaceTop = triggerRect.top;
    const spaceRight = viewportWidth - triggerRect.right;
    const spaceBottom = viewportHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;

    this.debugLog('Available space', { spaceTop, spaceRight, spaceBottom, spaceLeft });
    this.debugLog('Panel dimensions', { width: panelRect.width, height: panelRect.height });

    // Define a small tolerance for horizontal positions (3% of viewport width)
    const horizontalTolerance = viewportWidth * 0.03;

    // The horizontal positions are preferred for most tooltips
    const preferredOrder: ('left' | 'right' | 'top' | 'bottom')[] = ['left', 'right', 'top', 'bottom'];

    // Check which positions have enough space (adding tolerance for horizontal positions)
    const positionsWithSpace: ('left' | 'right' | 'top' | 'bottom')[] = [];

    // Check horizontal positions first with tolerance
    if (spaceLeft + horizontalTolerance >= panelRect.width + offsetPx) {
      positionsWithSpace.push('left');
    }
    if (spaceRight + horizontalTolerance >= panelRect.width + offsetPx) {
      positionsWithSpace.push('right');
    }

    // Then check vertical positions
    if (spaceTop >= panelRect.height + offsetPx) {
      positionsWithSpace.push('top');
    }
    if (spaceBottom >= panelRect.height + offsetPx) {
      positionsWithSpace.push('bottom');
    }

    this.debugLog('Positions with space', positionsWithSpace);

    // Determine the best position
    let bestPosition = this.defaultPosition;

    // If default position has space, use it
    if (positionsWithSpace.includes(this.defaultPosition)) {
      bestPosition = this.defaultPosition;
    }
    // Otherwise use the first position with enough space, prioritizing by preferred order
    else if (positionsWithSpace.length > 0) {
      // Find the highest priority position that has space
      for (const position of preferredOrder) {
        if (positionsWithSpace.includes(position)) {
          bestPosition = position;
          break;
        }
      }
    }
    // If no position has enough space, fallback to default or first in preferred order
    else {
      // Just use the first preferred position as fallback
      bestPosition = preferredOrder[0];
    }

    this.debugLog('Selected position', bestPosition);

    // Set the final position and reset counters
    this._currentPosition = bestPosition;
    this._positionChangeCount = 0;

    // After selecting the position, check if we need minimal adjustments
    // to avoid going off-screen in edge cases
    requestAnimationFrame(() => {
      if (this._isOpen) {
        const finalRect = this.panelEl!.getBoundingClientRect();
        if (finalRect.left < 0 ||
            finalRect.right > viewportWidth ||
            finalRect.top < 0 ||
            finalRect.bottom > viewportHeight) {
          this.applyMinimalAdjustments(finalRect, viewportWidth, viewportHeight);
        }
      }
    });

    // Reset panel styles to apply new position
    this.panelEl.style.visibility = '';
    this.panelEl.style.display = '';
  }

  /**
   * Apply minimal adjustments to keep tooltip within viewport
   * Only used as a last resort if no position works
   */
  private applyMinimalAdjustments(panelRect: DOMRect, viewportWidth: number, viewportHeight: number) {
    if (!this.panelEl) return;

    // Only make minimal adjustments if really necessary
    let transformX = 0;
    let transformY = 0;

    // Horizontal adjustments - only if absolutely necessary
    if (panelRect.left < 0) {
      transformX = Math.abs(panelRect.left) + 5;
    } else if (panelRect.right > viewportWidth) {
      transformX = -(panelRect.right - viewportWidth + 5);
    }

    // Vertical adjustments - only if absolutely necessary
    if (panelRect.top < 0) {
      transformY = Math.abs(panelRect.top) + 5;
    } else if (panelRect.bottom > viewportHeight) {
      transformY = -(panelRect.bottom - viewportHeight + 5);
    }

    // Apply transforms only if needed
    if (transformX !== 0 || transformY !== 0) {
      const transform = `translate(${transformX}px, ${transformY}px)`;
      this.debugLog('Applying position adjustment', { transformX, transformY });
      this.panelEl.style.transform = transform;
    }
  }

  private showTooltip() {
    if (this._isOpen || !this.panelEl || !this.triggerEl) return;
    this.debugLog('Showing tooltip');

    // Calculate the best position before showing the tooltip
    this.calculatePosition();

    this._isOpen = true;

    // Ensure display is set to block to make the tooltip visible
    if (this.panelEl) {
      this.panelEl.style.display = 'block';
    }

    this.dispatchEvent(new CustomEvent('outline-tooltip-show', {
      detail: { host: this },
      bubbles: true,
      composed: true
    }));
  }

  private hideTooltip() {
    if (!this._isOpen) return;
    this.debugLog('Hiding tooltip');

    this._isOpen = false;

    this.dispatchEvent(new CustomEvent('outline-tooltip-hide', {
      detail: { host: this },
      bubbles: true,
      composed: true
    }));

    // Ensure display is set to none after transition ends to prevent scrolling
    if (this.panelEl) {
      // Use setTimeout to wait for the transition to complete
      setTimeout(() => {
        if (!this._isOpen && this.panelEl) {
          this.panelEl.style.display = 'none';
        }
      }, 200); // Match the CSS transition time
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('defaultPosition') && !this._isOpen) {
      this._currentPosition = this.defaultPosition;
      // Reset position adjustments only when we're changing the default position
      // and the tooltip is closed
      this.resetPositionAdjustments();
      this.debugLog('Default position changed', this.defaultPosition);
    }

    if (changedProperties.has('debug')) {
      this.debugLog('Debug mode changed', this.debug);
    }
  }

  render() {
    this.debugLog('Rendering', {
      isOpen: this._isOpen,
      position: this._currentPosition
    });

    return html`
      <div
        id="trigger"
        part="trigger"
        tabindex="0"
        aria-describedby=${this._isOpen ? ifDefined(this.tooltipId) : nothing}
      >
        <slot @slotchange=${this.handleSlotChange}></slot>
      </div>
      <div
        id=${ifDefined(this.tooltipId)}
        part="panel"
        role="tooltip"
        class="tooltip-panel position-${this._currentPosition} ${this._isOpen ? 'open' : ''}"
      >
        <slot name="tooltip-content"></slot>
      </div>
    `;
  }

  private handleSlotChange() {
    this.debugLog('Slot content changed');
    this.requestUpdate();
  }

  private resetPositionAdjustments() {
    if (!this.panelEl) return;
    this.debugLog('Resetting position adjustments');
    this.panelEl.style.transform = '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-tooltip': OutlineTooltip;
  }
}
